import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Clock, 
  Users, 
  BookOpen, 
  Settings, 
  LogOut,
  Bell,
  Search,
  Plus,
  Edit3,
  Video,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const TeacherDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Mock teacher data
  const teacher = {
    name: "Sarah Mitchell",
    email: "sarah.mitchell@lexora.com",
    avatar: "👩‍🏫",
    tier: "Premium Teacher",
    rating: 4.9,
    totalClasses: 156
  };

  // Mock upcoming classes
  const upcomingClasses = [
    {
      id: 1,
      student: "Carlos Rodriguez",
      time: "10:00 AM",
      date: "Today",
      duration: "55 min",
      type: "Business English",
      meetingLink: "https://meet.google.com/abc-def-ghi",
      studentLevel: "Intermediate",
      notes: "Focus on presentation skills"
    },
    {
      id: 2,
      student: "Maria Lopez",
      time: "2:00 PM", 
      date: "Today",
      duration: "85 min",
      type: "Conversation Practice",
      meetingLink: "https://meet.google.com/xyz-abc-def",
      studentLevel: "Advanced",
      notes: "Prepare for job interview"
    },
    {
      id: 3,
      student: "Jorge Martinez",
      time: "9:00 AM",
      date: "Tomorrow",
      duration: "55 min",
      type: "Grammar Focus",
      meetingLink: "https://meet.google.com/def-ghi-jkl",
      studentLevel: "Beginner",
      notes: "Past tense review"
    }
  ];

  // Mock weekly stats
  const weeklyStats = {
    classesThisWeek: 12,
    hoursThisWeek: 14.5,
    studentsThisWeek: 8,
    earnings: "€980"
  };

  const navigation = [
    { id: 'dashboard', label: 'Dashboard', icon: Calendar },
    { id: 'availability', label: 'Availability', icon: Clock },
    { id: 'students', label: 'Students', icon: Users },
    { id: 'notes', label: 'Class Notes', icon: BookOpen },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  const Sidebar = () => (
    <div className="w-64 bg-white border-r border-gray-200 h-screen flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold">L</span>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Lexora</h1>
            <p className="text-sm text-gray-500">Teacher Portal</p>
          </div>
        </div>
      </div>

      {/* Teacher Profile */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-2xl">
            👩‍🏫
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{user?.name || 'Teacher'}</h3>
            <p className="text-sm text-blue-600">Premium Teacher</p>
            <div className="flex items-center space-x-1 mt-1">
              <span className="text-yellow-400">⭐</span>
              <span className="text-sm text-gray-600">{teacher.rating}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <li key={item.id}>
                <button
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                    isActive 
                      ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-gray-200">
        <button 
          onClick={onLogout}
          className="w-full flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">Logout</span>
        </button>
      </div>
    </div>
  );

  const DashboardContent = () => (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Welcome back, {teacher.name.split(' ')[0]}!</h1>
        <p className="text-gray-600">Here's what's happening with your classes today.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { label: "Classes This Week", value: weeklyStats.classesThisWeek, icon: Calendar, color: "blue" },
          { label: "Hours This Week", value: weeklyStats.hoursThisWeek, icon: Clock, color: "green" },
          { label: "Students This Week", value: weeklyStats.studentsThisWeek, icon: Users, color: "purple" },
          { label: "Earnings", value: weeklyStats.earnings, icon: BookOpen, color: "orange" }
        ].map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 bg-${stat.color}-100 rounded-lg flex items-center justify-center`}>
                  <Icon className={`w-6 h-6 text-${stat.color}-600`} />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Today's Schedule */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Today's Schedule</h2>
            <span className="text-sm text-gray-500">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
          </div>
        </div>
        
        <div className="p-6">
          <div className="space-y-4">
            {upcomingClasses.filter(cls => cls.date === 'Today').map((cls) => (
              <div key={cls.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{cls.student}</h3>
                    <p className="text-sm text-gray-600">{cls.type} • {cls.studentLevel}</p>
                    <p className="text-sm text-gray-500">{cls.time} • {cls.duration}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2">
                    <Video className="w-4 h-4" />
                    <span>Join Call</span>
                  </button>
                  <button className="text-gray-400 hover:text-gray-600">
                    <Edit3 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <button className="w-full flex items-center space-x-3 p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <Plus className="w-5 h-5 text-blue-600" />
              <span>Set Availability</span>
            </button>
            <button className="w-full flex items-center space-x-3 p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <BookOpen className="w-5 h-5 text-green-600" />
              <span>Add Class Notes</span>
            </button>
            <button className="w-full flex items-center space-x-3 p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <Calendar className="w-5 h-5 text-purple-600" />
              <span>Sync Calendar</span>
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-600">Completed class with Maria Lopez</span>
            </div>
            <div className="flex items-center space-x-3">
              <Calendar className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-gray-600">New booking from Jorge Martinez</span>
            </div>
            <div className="flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              <span className="text-sm text-gray-600">Reminder: Update availability for next week</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardContent />;
      case 'availability':
        return <AvailabilityContent />;
      case 'students':
        return <StudentsContent upcomingClasses={upcomingClasses} />;
      case 'notes':
        return <NotesContent upcomingClasses={upcomingClasses} />;
      case 'settings':
        return <SettingsContent teacher={teacher} />;
      default:
        return <DashboardContent />;
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="p-6">
          {renderContent()}
        </div>
      </main>
    </div>
  );
};

// Availability Content Component
const AvailabilityContent = () => {
  const [selectedTimes, setSelectedTimes] = useState({
    monday: ['09:00', '10:00', '14:00', '15:00'],
    tuesday: ['09:00', '10:00', '11:00', '14:00', '15:00'],
    wednesday: ['10:00', '11:00', '14:00', '15:00', '16:00'],
    thursday: ['09:00', '10:00', '14:00', '15:00'],
    friday: ['09:00', '10:00', '11:00', '14:00'],
    saturday: [],
    sunday: []
  });

  const timeSlots = [
    '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', 
    '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'
  ];

  const days = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  const toggleTimeSlot = (day, time) => {
    setSelectedTimes(prev => ({
      ...prev,
      [day]: prev[day].includes(time) 
        ? prev[day].filter(t => t !== time)
        : [...prev[day], time].sort()
    }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Set Your Availability</h1>
        <p className="text-gray-600">Choose the times when you're available to teach classes.</p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="grid grid-cols-1 gap-6">
          {days.map((day) => (
            <div key={day.key} className="border-b border-gray-100 pb-6 last:border-b-0">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{day.label}</h3>
              <div className="grid grid-cols-6 md:grid-cols-8 lg:grid-cols-13 gap-2">
                {timeSlots.map((time) => {
                  const isSelected = selectedTimes[day.key].includes(time);
                  return (
                    <button
                      key={time}
                      onClick={() => toggleTimeSlot(day.key, time)}
                      className={`p-2 rounded-lg text-sm font-medium transition-colors ${
                        isSelected
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {time}
                    </button>
                  );
                })}
              </div>
              <p className="text-sm text-gray-500 mt-2">
                {selectedTimes[day.key].length} time slots selected
              </p>
            </div>
          ))}
        </div>

        <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Total weekly availability: {Object.values(selectedTimes).flat().length} hours
          </div>
          <div className="space-x-3">
            <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
              Reset
            </button>
            <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Save Availability
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Students Content Component  
const StudentsContent = ({ upcomingClasses }) => (
  <div className="space-y-6">
    <div>
      <h1 className="text-2xl font-bold text-gray-900">Your Students</h1>
      <p className="text-gray-600">Manage your upcoming classes and student information.</p>
    </div>

    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Upcoming Classes</h2>
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search students..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        {upcomingClasses.map((cls) => (
          <div key={cls.id} className="p-6 hover:bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                  {cls.student.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{cls.student}</h3>
                  <p className="text-sm text-gray-600">{cls.type} • {cls.studentLevel}</p>
                  <p className="text-sm text-gray-500">{cls.date} at {cls.time} • {cls.duration}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">Class Notes</p>
                  <p className="text-sm text-gray-600">{cls.notes}</p>
                </div>
                <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                  View Details
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Notes Content Component
const NotesContent = ({ upcomingClasses }) => {
  const [selectedClass, setSelectedClass] = useState(null);
  const [noteText, setNoteText] = useState('');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Class Notes</h1>
        <p className="text-gray-600">Add notes after completing classes with your students.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Class List */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Recent Classes</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {upcomingClasses.map((cls) => (
              <button
                key={cls.id}
                onClick={() => setSelectedClass(cls)}
                className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                  selectedClass?.id === cls.id ? 'bg-blue-50 border-r-4 border-blue-600' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{cls.student}</h3>
                    <p className="text-sm text-gray-600">{cls.type}</p>
                    <p className="text-sm text-gray-500">{cls.date} • {cls.time}</p>
                  </div>
                  <div className="text-right">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Completed
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Notes Editor */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {selectedClass ? `Notes for ${selectedClass.student}` : 'Select a Class'}
            </h2>
          </div>
          {selectedClass ? (
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Class Summary
                  </label>
                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="What did you cover in this class? How did the student perform?"
                    className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Student Progress
                  </label>
                  <select className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option>Excellent progress</option>
                    <option>Good progress</option>
                    <option>Steady progress</option>
                    <option>Needs improvement</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Homework/Next Steps
                  </label>
                  <textarea
                    placeholder="What should the student practice before the next class?"
                    className="w-full h-24 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <button className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors">
                  Save Notes
                </button>
              </div>
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">
              <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Select a class from the list to add notes</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Settings Content Component
const SettingsContent = ({ teacher }) => (
  <div className="space-y-6">
    <div>
      <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
      <p className="text-gray-600">Manage your profile and preferences.</p>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Profile Settings */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Profile Information</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
            <input
              type="text"
              defaultValue={teacher.name}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              type="email"
              defaultValue={teacher.email}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Teacher Tier</label>
            <select className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option>Premium Teacher</option>
              <option>Standard Teacher</option>
            </select>
          </div>
          <button className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors">
            Update Profile
          </button>
        </div>
      </div>

      {/* Calendar Integration */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Calendar Integration</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Google Calendar</h3>
                <p className="text-sm text-gray-600">Sync your availability</p>
              </div>
            </div>
            <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
              Connected
            </button>
          </div>
          
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-4 h-4 text-gray-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">iCal</h3>
                <p className="text-sm text-gray-600">Alternative calendar sync</p>
              </div>
            </div>
            <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors">
              Connect
            </button>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Calendar Sync Status</h4>
            <p className="text-sm text-blue-700">Your Google Calendar is successfully synced. New bookings will automatically appear in your calendar.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default TeacherDashboard;